/// <amd-module name="TFS/Admin/Views/OrganizationProjects/OrganizationProjectsRestoreTable" />
import React = require("react");

import AdminResources = require("TFS/Admin/Views/OrganizationProjects/Resources");

import { ColumnSelect, ColumnSorting, ITableColumn, ITableRowDetails, SimpleTableCell, SortOrder, Table, TableRow, sortItems } from "VSSUI/Table";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { OrganizationProjectReference, OrganizationProjectsConstants } from "TFS/Admin/Views/OrganizationProjects/Contracts/DataModels";
import { Pill, PillSize } from "VSSUI/Pill";

import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Button } from "VSSUI/Button";
import { Components } from "VSS/Platform/Layout";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { PillGroup } from "VSSUI/PillGroup";
import { ProjectVisibility } from "Tfs/Platform/Contracts/Core";
import { SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { Tooltip } from "VSSUI/TooltipEx";
import { VssPersona } from "VSSUI/VssPersona";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { routeUrl } from "VSS/Platform/Location";

export interface IProjectsRestoreTableProps extends IFilterablePivotProps, IFilterablePivotOwnProps {
    onMultiProjectRestore: (projects: OrganizationProjectReference[]) => void;
    OnRestoreProject: (project: OrganizationProjectReference) => void;
    OnSearchProject: (projects: OrganizationProjectReference[], newKeyword?: string, oldKeyword?: string, scopeDescriptor?: string) => void;
    Projects: OrganizationProjectReference[];
    IsInProgress?: boolean;
    ClearSelection: ObservableValue<boolean>;
}

export class ProjectsRestoreTableComponent extends FilterablePivot<IProjectsRestoreTableProps> {
    private _itemProvider: IObservableArray<OrganizationProjectReference>;
    private _columnSelected?: number;
    private _sortOrderSelected?: SortOrder;
    private _selection = new ListSelection({ selectOnFocus: false, multiSelect: true });
    private _showMultiRestoreButton = new ObservableValue<boolean>(false);
    private _sortedTableItemProvider: ObservableValue<ArrayItemProvider<OrganizationProjectReference>> = new ObservableValue<
        ArrayItemProvider<OrganizationProjectReference>
    >(new ArrayItemProvider([]));
    private _selectionLock: number = 0;

    public constructor(props: IProjectsRestoreTableProps) {
        super(props);
        this._itemProvider = new ObservableArray<OrganizationProjectReference>(this.props.Projects);
        this._columnSelected = 1;
        this._sortOrderSelected = SortOrder.ascending;
    }

    public componentDidUpdate() {
        this._selection.subscribe(this._onSelectionChange);
        this.props.ClearSelection.subscribe(this._onClearSelection);
    }

    /***Filterable Pivot Methods */
    protected _getItemsName(): string {
        return AdminResources.Project;
    }

    protected _isContentReady(): boolean {
        return !this.props.IsInProgress && !this._isFiltering();
    }

    protected _getFilteredContentColumns(): ITableColumn<OrganizationProjectReference>[] {
        return this._columns;
    }

    protected _onRenderRow(index: number, item: OrganizationProjectReference, details: ITableRowDetails<OrganizationProjectReference>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        return this._getRenderElements(this._itemProvider.value, this._columns);
    }

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        return this._getRenderElements(resultItems, this._getFilteredContentColumns(), resultItems.length < 1);
    }

    protected _onFilterChanged(keyword: string): void {
        this._selection.clear();
        this.props.OnSearchProject(this.props.Projects, keyword, this.props.searchResult ? this.props.searchResult.keyword : "");
    }

    protected _isProjectScope(): boolean {
        return false;
    }

    protected _onExpandSearch(): void {}

    protected _isCollectionNavigationAllowed(): boolean {
        return false;
    }
    /***End Filterable Pivot Methods */

    protected _getRenderElements(
        items: OrganizationProjectReference[],
        columns: ITableColumn<OrganizationProjectReference>[],
        zeroFilteredData?: boolean
    ): JSX.Element {
        if (this._columnSelected != undefined && this._sortOrderSelected != undefined) {
            this._sortedTableItemProvider.value = new ArrayItemProvider(
                sortItems(this._columnSelected, this._sortOrderSelected, this.sortFunctions, this._columns, items)
            );
        } else {
            this._sortedTableItemProvider.value = new ArrayItemProvider(items);
        }

        const totalProjectCount = this.props.Projects.length;
        const totalFilteredResultCount = this.props.searchResult.resultItems.length;

        // Disable the selection column when there is no project to select for multi-restore
        // Keep track of the lock count because selection is unlocked only if net count == 0
        if (!totalProjectCount || (this.props.searchResult.status == SearchStatus.Completed && !totalFilteredResultCount)) {
            if (!this._selectionLock) {
                this._selection.lock();
                this._selectionLock--;
            }
        } else {
            if (this._selectionLock < 0) {
                this._selection.unlock();
                this._selectionLock++;
            }
        }

        return (
            <div>
                <CustomHeader className="bolt-header-with-commandbar">
                    <HeaderTitleArea className="flex-row table-title-text">
                        <>{this._showProjectAndFilterCount(totalProjectCount, totalFilteredResultCount)}</>
                    </HeaderTitleArea>
                    <Observer showMultiRestoreButton={this._showMultiRestoreButton}>
                        {(props: { showMultiRestoreButton: boolean }) => (
                            <Button
                                className="header-command-bar-menu-button create-button"
                                iconProps={{ iconName: "Refresh" }}
                                ariaLabel="restore"
                                onClick={() => this._onMultiRestoreClicked()}
                                text={AdminResources.RestoreProjects}
                                disabled={!props.showMultiRestoreButton}
                                primary={true}
                                key="multi-restore-button"
                            />
                        )}
                    </Observer>
                </CustomHeader>
                <div className="flex-grow project-column table-minimum-card-height">
                    <Observer itemProvider={this._sortedTableItemProvider}>
                        {(observableProps: { itemProvider: ArrayItemProvider<OrganizationProjectReference> }) => (
                            <Table<OrganizationProjectReference>
                                behaviors={[this.sortingBehavior]}
                                itemProvider={observableProps.itemProvider}
                                columns={columns}
                                selectRowOnClick={false}
                                selection={this._selection}
                            />
                        )}
                    </Observer>
                    {zeroFilteredData && this._getZeroData()}
                </div>
            </div>
        );
    }

    private _columns: ITableColumn<OrganizationProjectReference>[] = [
        new ColumnSelect(),
        {
            id: "project",
            name: AdminResources.ProjectName,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-40),
            renderCell: this.renderProject.bind(this),
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "description",
            name: AdminResources.ProjectDescription,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-60),
            renderCell: this.renderDescription.bind(this),
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "deletedTime",
            name: AdminResources.ProjectDeletedDate,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: this.renderDeletedTime.bind(this),
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "process",
            name: AdminResources.Process,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: this.renderProcess.bind(this),
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "visibility",
            name: AdminResources.ProjectVisibility,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-15),
            renderCell: this.renderVisibility.bind(this),
            sortProps: {
                ariaLabelAscending: AdminResources.SortedAZ,
                ariaLabelDescending: AdminResources.SortedZA
            }
        },
        {
            id: "restoreButton",
            width: new ObservableValue(42),
            renderCell: this.renderRestoreButton.bind(this)
        }
    ];

    private renderProject(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <SimpleTableCell key={"row" + rowIndex + "col" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                <VssPersona
                    className="icon-medium-margin"
                    identityDetailsProvider={{
                        getIdentityImageUrl: (size: number): string | undefined => {
                            return tableItem.defaultTeamImageUrl && tableItem.defaultTeamImageUrl.length ? tableItem.defaultTeamImageUrl : undefined;
                        },
                        getDisplayName: (): string => {
                            return tableItem.name;
                        }
                    }}
                    size={"medium"}
                />
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis">{tableItem.name}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private renderDescription(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return this.returnSimpleCell("row" + rowIndex + "col" + columnIndex, columnIndex, tableColumn, tableItem.description);
    }
    private renderDeletedTime(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return this.returnSimpleCell(
            "row" + rowIndex + "col" + columnIndex,
            columnIndex,
            tableColumn,
            this._getDeletedDate(tableItem.lastUpdateTime)
        );
    }
    private renderVisibility(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return this.returnSimpleCell(
            "row" + rowIndex + "col" + columnIndex,
            columnIndex,
            tableColumn,
            this._getProjectVisibility(tableItem.visibility)
        );
    }
    private renderProcess(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        const processTemplateName = tableItem.processTemplateName != undefined ? tableItem.processTemplateName : "";
        const processUrl = routeUrl(this.props.pageContext, OrganizationProjectsConstants.UrlRouterService, {
            _a: OrganizationProjectsConstants.routerWorkItem,
            "process-name": processTemplateName
        });

        return this.returnSimpleCell("row" + rowIndex + "col" + columnIndex, columnIndex, tableColumn, processTemplateName, processUrl);
    }
    private returnSimpleCell(key: string, columnIndex: number, tableColumn: ITableColumn<OrganizationProjectReference>, text: string, url?: string) {
        return (
            <SimpleTableCell key={key} columnIndex={columnIndex} tableColumn={tableColumn}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">
                        {(url != undefined && (
                            <Link href={url} target="_blank" rel="noopener noreferrer" aria-label={text}>
                                {text}
                            </Link>
                        )) ||
                            text}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private renderRestoreButton(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<OrganizationProjectReference>,
        tableItem: OrganizationProjectReference
    ) {
        return (
            <td
                aria-colindex={columnIndex + 1}
                className="bolt-table-cell-side-action bolt-table-cell bolt-list-cell"
                data-column-index={columnIndex}
                key="col-restore"
            >
                <div className="bolt-table-cell-content-reveal flex-row justify-center">
                    <Button
                        subtle={true}
                        tooltipProps={{ text: AdminResources.Restore }}
                        className="bolt-table-button-more"
                        iconProps={{ iconName: "Refresh", size: IconSize.medium }}
                        onClick={() => this.props.OnRestoreProject(tableItem)}
                    />
                </div>
            </td>
        );
    }

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<OrganizationProjectReference>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private sortingBehavior = new ColumnSorting<OrganizationProjectReference>((columnIndex: number, proposedSortOrder: SortOrder) => {
        this._columnSelected = columnIndex;
        this._sortOrderSelected = proposedSortOrder;
        this._selection.clear();
        this._sortedTableItemProvider.value = new ArrayItemProvider(
            sortItems(
                this._columnSelected,
                this._sortOrderSelected,
                this.sortFunctions,
                this._columns,
                this.props.searchResult.status == SearchStatus.Completed ? this.props.searchResult.resultItems : this.props.Projects
            )
        );
    });

    private sortFunctions = [
        null,
        // Sort on Project column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return item1.name!.localeCompare(item2.name!, navigator.language);
        },
        // Sort on Description column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            var description1 = item1.description ? item1.description : "";
            var description2 = item2.description ? item2.description : "";
            return description1.localeCompare(description2, navigator.language);
        },
        // Sort on Last Updated column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return item1.lastUpdateTime.getTime() > item2.lastUpdateTime.getTime() ? 1 : -1;
        },
        // Sort on Process column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            var process1 = item1.processTemplateName ? item1.processTemplateName : "";
            var process2 = item2.processTemplateName ? item2.processTemplateName : "";
            return process1.localeCompare(process2, navigator.language);
        },
        // Sort on Visibility column
        (item1: OrganizationProjectReference, item2: OrganizationProjectReference): number => {
            return this._getProjectVisibility(item1.visibility).localeCompare(this._getProjectVisibility(item2.visibility), navigator.language);
        },
        null
    ];

    private _getDeletedDate(editDate: Date) {
        return editDate.toLocaleDateString(navigator.language);
    }
    private _getProjectVisibility(visibility: ProjectVisibility) {
        switch (visibility) {
            case ProjectVisibility.Organization:
                return AdminResources.OrganizationVisibilityTitle;
            case ProjectVisibility.Private:
                return AdminResources.PrivateVisibilityTitle;
            case ProjectVisibility.Public:
                return AdminResources.PublicVisibilityTitle;
            default:
                return AdminResources.SystemPrivate;
        }
    }

    private _showProjectAndFilterCount(totalProjectCount: number, totalFilteredResultCount: number) {
        this.props.searchResult.status == SearchStatus.Completed &&
            announce(format(AdminResources.TableFilterResultsCount, totalFilteredResultCount));
        return (
            <PillGroup>
                <span className="text-ellipsis">{AdminResources.ProjectTotal}</span>
                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                    {totalProjectCount}
                </Pill>
                {this.props.searchResult.status == SearchStatus.Completed && (
                    <div className="flex-row">
                        <span className="text-ellipsis">{AdminResources.FilteredProjectTotal}</span>
                        <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                            {totalFilteredResultCount}
                        </Pill>
                    </div>
                )}
            </PillGroup>
        );
    }

    private _onMultiRestoreClicked = () => {
        var selectedProjectsToRestore: OrganizationProjectReference[] = [];
        if (this._selection.value.length > 0) {
            for (const range of this._selection.value) {
                for (let i = range.beginIndex; i <= range.endIndex; i++) {
                    const selectedItem = this._sortedTableItemProvider.value.value[i];
                    if (selectedItem) {
                        selectedProjectsToRestore.push(selectedItem);
                    }
                }
            }
        }
        this.props.onMultiProjectRestore(selectedProjectsToRestore);
    };

    private _onSelectionChange = (value: ISelectionRange[], action: string) => {
        this._showMultiRestoreButton.value = this._selection.selectedCount > 0 && this.props.Projects.length > 0;
    };

    private _onClearSelection = (value: boolean, action: string) => {
        if (value == true) {
            this._selection.clear();
        }
    };
}

Components.add("projectsRestoreTable", ProjectsRestoreTableComponent);
